import Typography from '@mui/material/Typography';
import { rootUiActions } from 'app-components/flux/root-ui';
import StdDialog from 'components/Dialogs/StdDialog';
import { createWatchlistModal } from 'components/Dialogs/WatchlistModal/actions';
import QButton from 'components/QButton';
import useQPreferences from 'components/QPreferences/useQPreferences';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CategoriesIcon from 'assets/icons/watchlist/category.svg';
import PayeeIcon from 'assets/icons/watchlist/payee.svg';
import TagsIcon from 'assets/icons/watchlist/tags.svg';
import WatchlistGraphicExampleDark from 'assets/images/watchlist/graphic-dark.svg';
import WatchlistGraphicExampleLight from 'assets/images/watchlist/graphic-light.svg';
import WatchlistExampleDark from 'assets/images/watchlist/list-card-dark.svg';
import WatchlistExampleLight from 'assets/images/watchlist/list-card-light.svg';

const useStyles = makeStyles()((theme) => ({
  skyBox: {
    height: 161,
    width: '100%',
    backgroundColor: theme.palette.info.light,
    userSelect: 'none',
    borderRadius: 8,
    margin: 24,
  },
  paddingTop: {
    paddingTop: 24,
  },
  root: {
    maxHeight: 489,
    minHeight: 489,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  restrictedWidth: {
    maxWidth: 600,
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    margin: '0 24px 24px 24px',
  },
  displayFlex: {
    display: 'flex',
    width: '100%',
  },
  colContainer: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    width: 'calc(33%)',
    margin: '0 12px 12px 12px',
    alignItems: 'center',
  },
  icon: {
    height: 48,
    width: 48,
  },
  boxInside: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  swlExample: {
    height: 250,
    width: 456,
    marginTop: 16,
    alignSelf: 'center',
  },
  margin40: {
    margin: 40,
    color: theme.palette.text.secondary,
  },
  bodyText: {
    margin: '40px 40px 0px 0',
    color: theme.palette.text.secondary,
  },
  swlGraphic: {
    height: 200,
    width: 600,
    marginTop: 16,
    alignSelf: 'center',
  },
  leftButton: {
    width: 170,
    marginRight: 12,
  },
  rightButton: {
    width: 170,
  },
  description: {
    maxWidth: 200,
    color: theme.palette.text.secondary,
  },
}));

function WatchlistFirstUse() {
  const [screenIndex, setScreenIndex] = useState(0);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const preferences = useQPreferences();
  const theme = useTheme();

  let WatchlistGraphicExample = WatchlistGraphicExampleLight;
  let WatchlistExample = WatchlistExampleLight;

  if (theme.palette.mode === 'dark') {
    WatchlistGraphicExample = WatchlistGraphicExampleDark;
    WatchlistExample = WatchlistExampleDark;
  }

  const onClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog());
  }, [dispatch]);

  const submit = useCallback(() => {
    preferences.setDatasetPreference({ watchlistSetupCompleted: true });
    onClose();
  }, [preferences, onClose]);

  const emptyFunc = () => {};

  const screens = useMemo(() => {
    const retArr: any[] = [];
    retArr.push(
      <>
        <div className={classes.root}>
          <Typography variant='titleRegular'>
            Create Watchlists to monitor specific spending-trends over time.
          </Typography>
          <div className={classes.restrictedWidth}>
            <Typography variant='bodyRegular' style={{ paddingTop: 24 }}>
              You can track spending by:
            </Typography>
          </div>
          <div className={classes.displayFlex}>
            <div className={classes.colContainer}>
              <div className={classes.skyBox}>
                <div className={classes.boxInside}>
                  <img src={CategoriesIcon} alt={'cat-icon'} className={classes.icon} />
                  <Typography className={classes.paddingTop}>Category</Typography>
                </div>
              </div>
              <Typography variant='paragraphRegular' className={classes.description}>
                Follow your spending across an entire category, like Coffee Shops, Travel, or Entertainment.{' '}
              </Typography>
            </div>
            <div className={classes.colContainer}>
              <div className={classes.skyBox}>
                <div className={classes.boxInside}>
                  <img src={PayeeIcon} alt={'payee-icon'} className={classes.icon} />
                  <Typography className={classes.paddingTop}>Payee</Typography>
                </div>
              </div>
              <Typography variant='paragraphRegular' className={classes.description}>
                Keep track of how much you spend with a specific business or merchant.
              </Typography>
            </div>
            <div className={classes.colContainer}>
              <div className={classes.skyBox}>
                <div className={classes.boxInside}>
                  <img src={TagsIcon} alt={'tag-icon'} className={classes.icon} />
                  <Typography className={classes.paddingTop}>Tag</Typography>
                </div>
              </div>
              <Typography variant='paragraphRegular' className={classes.description}>
                Track spending across categories and payees by adding tags to specific transactions.
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.buttonsContainer}>
          <QButton onClick={() => setScreenIndex(1)} variant={'contained'}>
            Continue
          </QButton>
        </div>
      </>,
    );
    retArr.push(
      <>
        <div className={classes.root}>
          <Typography variant='titleRegular'>
            See your spending for the month and year, plus averages and projections.
          </Typography>
          <img src={WatchlistExample} alt={'watchlist-example'} className={classes.swlExample} />
          <Typography variant='bodyRegular' className={classes.bodyText}>
            For each Watchlist, you can see your monthly average and year to date spending, plus a breakdown of spending
            so far this month. You can also set an optional target amount to help keep yourself on track.{' '}
          </Typography>
        </div>
        <div className={classes.buttonsContainer}>
          <QButton onClick={() => setScreenIndex(2)} variant={'contained'}>
            Continue
          </QButton>
        </div>
      </>,
    );
    retArr.push(
      <>
        <div className={classes.root}>
          <Typography variant='titleRegular'>Set your sights on even more detailed information.</Typography>
          <img src={WatchlistGraphicExample} alt={'watchlist-graphic-example'} className={classes.swlGraphic} />
          <Typography variant='bodyRegular' className={classes.margin40}>
            Diving into a specific Watchlist shows spending over the last twelve months, the percentage spent by payee
            or category, and your individual transactions.
          </Typography>
        </div>
        <div className={classes.buttonsContainer}>
          <Button onClick={submit} variant={'outlined'} className={classes.leftButton}>
            Create Later
          </Button>
          <QButton
            onClick={() => {
              submit();
              dispatch(createWatchlistModal(null, false, emptyFunc));
            }}
            variant={'contained'}
            className={classes.rightButton}
          >
            Create Watchlist
          </QButton>
        </div>
      </>,
    );

    return retArr;
  }, [
    classes.root,
    classes.restrictedWidth,
    classes.displayFlex,
    classes.colContainer,
    classes.skyBox,
    classes.boxInside,
    classes.icon,
    classes.paddingTop,
    classes.description,
    classes.buttonsContainer,
    classes.swlExample,
    classes.bodyText,
    classes.swlGraphic,
    classes.margin40,
    classes.leftButton,
    classes.rightButton,
    WatchlistExample,
    WatchlistGraphicExample,
    submit,
    dispatch,
  ]);

  return (
    <StdDialog
      onBack={() => setScreenIndex(screenIndex - 1)}
      onClose={onClose}
      open
      sharedcomponentid={'WATCHLIST_FIRST_TIME_USE_DIALOG'}
      showBackButton={screenIndex !== 0}
      showCloseButton
      size='md'
      title='Get started with Watchlist'
    >
      {screens[screenIndex]}
    </StdDialog>
  );
}

export default WatchlistFirstUse;
